import { useState } from 'react';
import { Form, Formik } from 'formik';
import { isEmpty } from 'lodash';
import { parseNearAmount } from '@near-js/utils';

import CloseIcon from 'components/icons/CloseIcon';
import ArrowLeftIcon from 'components/icons/ArrowLeftIcon';
import EnterAmountView from './components/views/enter-amount-view';
import ConfirmView from './components/views/confirm-view';
import SuccessView from './components/views/success-view';

import { StyledContainer, StyledSection } from '../views.style';
import { StyledCloseButton, StyledBackButton } from './increase-storage-view.style';

import { useAppDispatch, useAppSelector } from 'redux/use-hooks';
import { selectActiveToken } from 'redux/slices/tokens';
import { actionAccountSetStorageBalance, selectAccountId } from 'redux/slices/account';
import { refreshAccount } from 'redux/actions/account';
import { actions as statusSliceActions } from 'redux/slices/status';
import { storageTabsOptions, VIEWS_STORAGE } from 'lib/constants';
import { validatioSchemaStorage } from '../send-view/form-modal/validation-schema';
import { fungibleTokensService } from 'services/fungible-tokens';
import { getStep } from 'utils';
import { getWalletActionErrorPresentation } from 'utils/wallet';
import { FormValues } from 'types';
import config from 'config';

const { clearLocalAlert, showCustomAlert } = statusSliceActions;
const { OCC_CONTRACT } = config;

const [
  myStorageTab,
  sendByIdTab,
] = storageTabsOptions;

const IncreaseStorageView = ({ closeModal } : { closeModal: () => void }) => {
  const [sendingToken, setSendingToken] = useState(false);
  const [activeTab, setActiveTab] = useState(myStorageTab);
  const [activeStep, setActiveStep] = useState(VIEWS_STORAGE.ENTER_AMOUNT);
  const currentValidationSchema = validatioSchemaStorage[activeStep];
  const isLastStep: boolean = activeStep === VIEWS_STORAGE.CONFIRM;
  const isSendByIdTab = activeTab.value === sendByIdTab.value;

  const dispatch = useAppDispatch();
  const accountId = useAppSelector(selectAccountId);
  const nearTokenBalance = useAppSelector(selectActiveToken);

  const initialValues = {
    amount: '',
    receiverId: '',
  };

  const goBack = () => setActiveStep((step) => getStep(step, -1, VIEWS_STORAGE));

  const goForward = () => setActiveStep((step) => getStep(step, +1, VIEWS_STORAGE));

  const submitHandler = async (
    values: FormValues,
    { touched, validateForm, setTouched, setSubmitting }: any
  ) => {
    const errs = await validateForm();

    if (!isEmpty(errs)) {
      setTouched({ ...touched, ...errs });
      return;
    }

    if (isLastStep) {
      setSendingToken(true);

      try {
        await fungibleTokensService.transferStorageDeposit({
          accountId,
          contractName: OCC_CONTRACT,
          receiverAccount: isSendByIdTab ? values.receiverId : accountId,
          storageDepositAmount: parseNearAmount(values.amount),
          registrationOnly: false,
        });
        setSendingToken(false);
        setSubmitting(false);
        goForward();
        dispatch(clearLocalAlert());
        dispatch(refreshAccount());
        dispatch(actionAccountSetStorageBalance({ contractName: OCC_CONTRACT, accountId }));
      } catch (e: any) {
        const presentation = getWalletActionErrorPresentation(e);
        dispatch(
          showCustomAlert({
            showAlert: true,
            success: false,
            messageCodeHeader: 'error',
            messageCode: presentation.messageCode,
            errorMessage: presentation.errorMessage
          })
        );
        setSendingToken(false);
      }
      return;
    }

    setSendingToken(false);
    goForward();
  };

  return (
    <StyledContainer>
      {activeStep === VIEWS_STORAGE.CONFIRM && (
        <StyledBackButton onClick={goBack}>
          <ArrowLeftIcon sm color="#222" />
        </StyledBackButton>
      )}
      <StyledCloseButton onClick={closeModal}>
        <CloseIcon color="#222" />
      </StyledCloseButton>
      <StyledSection p="48px">
        <Formik
          validateOnBlur={false}
          initialValues={initialValues}
          validationSchema={currentValidationSchema(nearTokenBalance, isSendByIdTab)}
          onSubmit={submitHandler}
        >
          {() => {
            return (
              <Form>
                {activeStep === VIEWS_STORAGE.ENTER_AMOUNT && (
                  <EnterAmountView
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                  />
                )}
                {activeStep === VIEWS_STORAGE.CONFIRM && (
                  <ConfirmView
                    activeTab={activeTab}
                    isLoading={sendingToken}
                  />
                )}
                {activeStep === VIEWS_STORAGE.SUCCESS && (
                  <SuccessView closeModal={closeModal} />
                )}
              </Form>
            )
          }}
        </Formik>
      </StyledSection>
    </StyledContainer>
  )
};

export default IncreaseStorageView;
